import React from "react"
import {
  Link,
  useTranslation,
  Trans,
  useI18next,
} from "@zestia/gatsby-plugin-react-i18next"
import { useStaticJobRoles } from "@/hooks/use-job-roles"
import Logomark from "@/icons/logomark.svg"
import PolygonUpTime from "@/icons/polygon-up-time.svg"
import { formatNumber } from "@/utils/format-number"

import FooterSecondary from "@/components/FooterSecondary"
import FullWidthCTABanner from "@/components/FullWidthCTABanner"

const Footer = ({ darkFooter, hideFooterLinks }) => {
  const { t } = useTranslation()
  const jobRoles = useStaticJobRoles()

  const { originalPath } = useI18next()

  const isBlog = originalPath.startsWith("/blog") || false

  return (
    <div className={`footer ${darkFooter ? "footer--dark" : ""}`}>
      {/* Footer CTA Banner */}
      {isBlog && <FullWidthCTABanner />}

      {!hideFooterLinks && (
        <footer className="container-fluid row footer__content-wrapper mt-16">
          <div className="col-xs-12 col-lg-2 mb-10 pr-4">
            <Link className="footer__logo mb-3" to="/" title="CapsuleCRM">
              <Logomark className="footer__logo-icon" />
            </Link>

            <p className="p--sm">
              <Trans
                i18nKey="footer.socials"
                components={{
                  Twitter: (
                    <a
                      className="text-link text-link--bare"
                      rel="external"
                      href="https://twitter.com/CapsuleCRM/"
                      target="_blank"
                    />
                  ),
                  Facebook: (
                    <a
                      className="text-link text-link--bare"
                      rel="external"
                      href="https://www.facebook.com/capsulecrm/"
                      target="_blank"
                    />
                  ),
                  Youtube: (
                    <a
                      className="text-link text-link--bare"
                      rel="external"
                      href="https://www.youtube.com/user/CapsuleCRM"
                      target="_blank"
                    />
                  ),
                  Linkedin: (
                    <a
                      className="text-link text-link--bare"
                      rel="external"
                      href="https://www.linkedin.com/company/capsulecrm/"
                      target="_blank"
                    />
                  ),
                }}
              />
            </p>
          </div>

          <div className="footer__links col-xs-12 col-lg-10">
            <div>
              <h6 className="mb-2">{t("footer.links.column-one.title")}</h6>
              <div className="footer__links-list">
                <Link className="footer__links-item" to="/features/">
                  {t("footer.links.column-one.features-overview")}
                </Link>

                <Link className="footer__links-item" to="/crm-comparisons/">
                  {t("footer.links.column-one.crm-comparison")}
                </Link>

                <Link className="footer__links-item" to="/features/mobile-app/">
                  {t("footer.links.column-one.ios-android")}
                </Link>

                <Link className="footer__links-item" to="/signup/">
                  {t("footer.links.column-one.pricing")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/security/"
                  language="en"
                >
                  {t("footer.links.column-one.security")}
                </Link>

                <a
                  className="footer__links-item"
                  target="_blank"
                  href="https://status.capsulecrm.com/"
                >
                  {t("footer.links.column-one.status")}
                  <span className="footer__links-label bg-grey-1">
                    <PolygonUpTime />{" "}
                    {formatNumber(0.9999, {
                      style: "percent",
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </a>

                <Link className="footer__links-item" to="/partner-program/">
                  {t("footer.links.column-one.partner-program")}
                </Link>

                <Link className="footer__links-item" to="/free-crm/">
                  {t("footer.links.column-one.free-plan")}
                </Link>
              </div>
            </div>

            <div>
              <h6 className="mb-2">{t("footer.links.column-two.title")}</h6>
              <div className="footer__links-list">
                <Link className="footer__links-item" to="/about/">
                  {t("footer.links.column-two.about")}
                </Link>

                <Link className="footer__links-item" to="/media/">
                  {t("footer.links.column-two.resources")}
                </Link>

                <Link className="footer__links-item" to="/careers/">
                  {t("footer.links.column-two.careers")}
                  {jobRoles.length > 0 && (
                    <span className="bg-blue-600 text-white footer__links-label">
                      {t("footer.links.column-two.hiring")}
                    </span>
                  )}
                </Link>

                <Link className="footer__links-item" to="/terms/" language="en">
                  {t("footer.links.column-two.terms")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/privacy/"
                  language="en"
                >
                  {t("footer.links.column-two.privacy")}
                </Link>

                <Link className="footer__links-item" to="/gdpr/" language="en">
                  {t("footer.links.column-two.gdpr")}
                </Link>

                <Link className="footer__links-item" to="/support/contact-us/">
                  {t("common.contact-us")}
                </Link>
              </div>
            </div>

            <div>
              <h6 className="mb-2">{t("footer.links.column-three.title")}</h6>
              <div className="footer__links-list">
                <a
                  className="footer__links-item"
                  target="_blank"
                  href="https://developer.capsulecrm.com/"
                >
                  {t("footer.links.column-three.api")}
                </a>

                <Link
                  className="footer__links-item"
                  to="/integrations/quickbooks-crm/"
                >
                  {t("footer.links.column-three.quickbooks")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/integrations/xero-crm/"
                >
                  {t("footer.links.column-three.xero")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/integrations/zapier-crm/"
                >
                  {t("footer.links.column-three.zapier")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/microsoft-365-integrations-crm/"
                >
                  {t("footer.links.column-three.microsoft")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/integrations/mailchimp-crm/"
                >
                  {t("footer.links.column-three.mailchimp")}
                </Link>

                <Link className="footer__links-item" to="/integrations/gmail/">
                  {t("footer.links.column-three.gmail")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/google-workspace-crm/"
                >
                  {t("footer.links.column-three.google-workspace")}
                </Link>

                <Link className="footer__links-item" to="/integrations/">
                  {t("footer.links.column-three.all-integrations")}
                </Link>
              </div>
            </div>

            <div>
              <h6 className="mb-2">{t("footer.links.column-four.title")}</h6>
              <div className="footer__links-list">
                <Link
                  className="footer__links-item"
                  to="/industries/crm-for-startups/"
                >
                  {t("footer.links.column-four.startups")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/industries/crm-small-business/"
                >
                  {t("footer.links.column-four.small-business")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/industries/crm-for-medium-sized-businesses/"
                >
                  {t("footer.links.column-four.mid-sized")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/industries/crm-for-finance/"
                >
                  {t("footer.links.column-four.finance")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/industries/crm-for-saas/"
                >
                  {t("footer.links.column-four.saas")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/industries/crm-for-photographers/"
                >
                  {t("footer.links.column-four.photographers")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/industries/crm-for-customer-service/"
                >
                  {t("footer.links.column-four.customer-service")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/industries/crm-for-freelancers/"
                >
                  {t("footer.links.column-four.freelancers")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/industries/crm-for-nonprofits/"
                >
                  {t("footer.links.column-four.nonprofits")}
                </Link>
                <Link className="footer__links-item" to="/industries/">
                  {t("footer.links.column-four.all-industries")}
                </Link>
              </div>
            </div>
            <div>
              <h6 className="mb-2">{t("footer.links.column-five.title")}</h6>
              <div className="footer__links-list">
                <Link className="footer__links-item" to="/support/">
                  {t("footer.links.column-five.support")}
                </Link>

                <Link className="footer__links-item" to="/blog/">
                  {t("footer.links.column-five.blog")}
                </Link>

                <Link className="footer__links-item" to="/resources/">
                  {t("footer.links.column-five.video-tutorials")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/resources/customer-stories/"
                >
                  {t("footer.links.column-five.customer-stories")}
                </Link>

                <Link
                  className="footer__links-item"
                  to="/features/sales-tracking-software/"
                >
                  {t("footer.links.column-five.sales-tracking-software")}
                </Link>

                <Link className="footer__links-item" to="/crm-software/">
                  {t("footer.links.column-five.capsule-crm")}
                </Link>

                <Link className="footer__links-item" to="/what-is-a-crm/">
                  {t("footer.links.column-five.what-is-a-crm")}
                </Link>

                <Link className="footer__links-item" to="/crm-glossary/">
                  {t("footer.links.column-five.crm-glossary")}
                </Link>
              </div>
            </div>
          </div>
        </footer>
      )}

      <FooterSecondary footerLinksHidden={hideFooterLinks} />
    </div>
  )
}

export default Footer
